import { useRef, useEffect, useState } from "react";
import jsQR from "jsqr";
import { X, Camera, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

interface BinScannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDetected: (text: string) => void;
  initialStream?: MediaStream | null;
  autoStart?: boolean;
}

export function BinScanner({ open, onOpenChange, onDetected, initialStream = null, autoStart = true }: BinScannerProps) {
  const { t } = useTranslation("harvest");
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    if (!open) {
      stop();
      return;
    }

    if (autoStart && !initialStream) {
      startCamera();
    }
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, autoStart]);

  useEffect(() => {
    if (!open || !initialStream || streamRef.current === initialStream) return;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
    }

    streamRef.current = initialStream;
    setError(null);
    setCameraReady(false);
    setStream(initialStream);
  }, [open, initialStream]);

  const stop = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
      setStream(null);
    }
    setCameraReady(false);
  };

  const startCamera = async () => {
    setError(null);
    setCameraReady(false);
    try {
      const ms = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      streamRef.current = ms;
      setStream(ms);
    } catch (err) {
      const e = err as Error;
      setError(
        e.name === "NotAllowedError"
          ? t("bin.cameraDenied")
          : t("bin.cameraError", { message: e.message })
      );
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!open || !stream || !video) return;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    video.srcObject = stream;
    video
      .play()
      .then(() => {
        setCameraReady(true);
        tick();
      })
      .catch((err) => {
        const e = err as Error;
        setError(t("bin.cameraError", { message: e.message }));
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stream]);

  const tick = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
    if (code && code.data) {
      stop();
      onDetected(code.data.trim());
      onOpenChange(false);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const handleClose = () => {
    stop();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            {t("bin.scanBinQr")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("bin.pointCamera")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <p className="text-center text-muted-foreground">{error}</p>
              <Button onClick={() => { setError(null); startCamera(); }} variant="outline">
                {t("bin.tryAgain")}
              </Button>
            </div>
          ) : (
            <>
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
                {!cameraReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-muted text-muted-foreground">
                    <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                      <Camera className="w-7 h-7 text-primary animate-pulse" />
                    </div>
                    <p className="text-sm text-center">
                      {t("bin.startingCamera", { defaultValue: "Starting camera…" })}
                    </p>
                  </div>
                )}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[70%] border-2 border-primary rounded-lg" />
                </div>
              </div>
              <p className="text-sm text-center text-muted-foreground">
                {t("bin.pointCamera")}
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleClose} className="flex-1">
                  <X className="w-4 h-4 mr-2" /> {t("bin.cancel")}
                </Button>
              </div>
            </>
          )}
        </div>

        <canvas ref={canvasRef} className="hidden" />
      </DialogContent>
    </Dialog>
  );
}
